import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import CreatorDashboardLayout from './views/pages/creatorPage/CreatorDashboard/layout';
import CreatorDashboard from './views/pages/creatorPage/CreatorDashboard/home/CreatorDashboard';
import ErrorBoundary from './ErrorBoundary';

const lazyWithRetry = (importer, key) =>
  lazy(async () => {
    const refreshKey = `lazy-retried:${key}`;
    try {
      return await importer();
    } catch (error) {
      const message = String(error?.message || '').toLowerCase();
      const isChunkLoadFailure =
        message.includes('failed to fetch dynamically imported module') ||
        message.includes('importing a module script failed') ||
        message.includes('chunkloaderror');
      const hasRetried = window.sessionStorage.getItem(refreshKey) === '1';

      // Recover once from stale asset hashes after a fresh deploy.
      if (isChunkLoadFailure && !hasRetried) {
        window.sessionStorage.setItem(refreshKey, '1');
        window.location.reload();
        return new Promise(() => {});
      }

      window.sessionStorage.removeItem(refreshKey);
      console.error(`Lazy import failed for ${key}`, error);
      throw error;
    }
  });

// IoT pages
const IoTDashboard = lazyWithRetry(() => import('./views/pages/iot/IoTDashboard'), 'IoTDashboard');
const PiBots = lazyWithRetry(() => import('./views/pages/iot/PiBots'), 'PiBots');

// AI Agent pages (Agent list + builder eager to avoid critical-route chunk fetch failures)
import AgentListPage from './views/pages/agents/AgentListPage';
import AgentBuilderPage from './views/pages/agents/AgentBuilderPage';
const AgentDetailPage = lazyWithRetry(() => import('./views/pages/agents/AgentDetailPage'), 'AgentDetailPage');
const KnowledgeBasePage = lazyWithRetry(() => import('./views/pages/agents/KnowledgeBasePage'), 'KnowledgeBasePage');
const ConversationLogPage = lazyWithRetry(() => import('./views/pages/agents/ConversationLogPage'), 'ConversationLogPage');
const AgentProfilePage = lazyWithRetry(() => import('./views/pages/agents/AgentProfilePage'), 'AgentProfilePage');

// Components and Pages
import DefaultLayout from './layout/DefaultLayout';
import MiEMarket from './views/pages/mieMarket/MiEMarket';
import withAuthentication from './views/session/withAuthentication'; // HOC for authenticated wrapping
import RequireAuth from './requireAuth'; // Auth guard for protected routes
// import ManageAuctions from './components/ManageAuctions';
// import ArtworkGridLayout from './components/ArtGridLayout';
import CreateAccount from './components/CreateAccount';
import CreatorPage from './views/pages/creatorPage/CreatorPage';
import Onboarding from './views/pages/creatorPage/CreatorComponents/Onboarding';
import LocalLogin from './views/pages/login/LocalLogin';
// Pages
import Login from './views/pages/login/Login';
import VerifyEmail from './views/pages/login/VerifyEmail';
// import Register from './views/pages/register/Register';
// import NFCLogin from './views/pages/nfcLogin/NFCLogin';
import Page404 from './views/pages/page404/Page404';
import Page500 from './views/pages/page500/Page500';
// import KeysLayout from './views/pages/apikeys/KeysLayout';
import AdminPage from './views/pages/adminPage/AdminPage';
import PublicDefaultLayout from './layout/PublicDefaultLayout';
import MyStuff from './views/pages/MyStuff/MyStuff';
// import MiEServerAPI from './views/pages/mieServerAPI/MiEServerAPI';
// import MiEScheduler from './views/pages/scheduleManager/SchedPage';
import PasswordForget from './views/pages/passwordForget/PasswordForget';
import OAuthCallback from './views/pages/oauth/OAuthCallback';
import HealthCheck from './views/pages/health/HealthCheck';
import StripeReturn from './views/pages/orders/OrdersPage';
import CartPage from './views/pages/cart/CartPage';
import Payments from './views/pages/creatorPage/CreatorDashboard/payments/Payments';
import Payouts from './views/pages/creatorPage/CreatorDashboard/payouts/Payouts';
import Customers from './views/pages/creatorPage/CreatorDashboard/customers/Customers';
import OrdersPage from './views/pages/orders/OrdersPage';
// Wrap DefaultLayout with authentication
const AuthenticatedDefaultLayout = withAuthentication(DefaultLayout);

const routeLoadingFallback = (
  <div className="text-center p-5">
    <div className="spinner-border" />
  </div>
);

const lazyRouteElement = (element, label) => (
  <ErrorBoundary
    fallbackRender={({ resetErrorBoundary }) => (
      <div className="text-center p-5">
        <h5>Couldn&apos;t load this page.</h5>
        <p className="mb-2 mb-3">{label || 'Please try again.'}</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={resetErrorBoundary}>
          Retry
        </button>
      </div>
    )}
  >
    <Suspense fallback={routeLoadingFallback}>{element}</Suspense>
  </ErrorBoundary>
);


const DashboardWrapper = () => {
  console.log('DashboardWrapper mounted');
  return (
    <div className="flex h-screen">
      <CreatorDashboardLayout>
        <CreatorDashboard />
      </CreatorDashboardLayout>
    </div>
  );
};

const routerConfig = (
  <Routes>
    {/* Public Routes */}
    <Route path="/" element={<Login />} />
    <Route path="/login" element={<Login />} />
    <Route path="/local-login" element={<LocalLogin />} />
    <Route path="/verify-email" element={<VerifyEmail />} />
    {/* <Route path="/register" element={<Register />} /> */}
    <Route path="/register-member" element={<CreateAccount />} />
    {/* <Route path="/register-ah" element={<CreateAccount userType="AH" />} />
    <Route path="/nfcLogin" element={<NFCLogin />} /> */}
    <Route path="/404" element={<Page404 />} />
    <Route path="/500" element={<Page500 />} />
    <Route path="/passwordforget" element={<PasswordForget />} />

    {/* Public Routes with DefaultLayout */}
    <Route element={<PublicDefaultLayout />}>
      <Route path="/marketplace/*" element={<MiEMarket />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/return" element={<StripeReturn />} />
      <Route path="/orders" element={<OrdersPage />} />
    </Route>

    {/* Protected Routes — all share AuthenticatedDefaultLayout (AppHeader + sidebar) */}
    <Route element={<RequireAuth><AuthenticatedDefaultLayout /></RequireAuth>}>
      <Route path="/dashboard/*">
        <Route path="return/:accountId" element={<CreatorPage />} />
        <Route path="creator" element={<CreatorPage />} />
        <Route path="creator/home" element={<DashboardWrapper />} />
        <Route path="creator/customers" element={<Customers />} />
        <Route path="creator/payments" element={<Payments />} />
        <Route path="creator/payouts" element={<Payouts />} />

        {/* IoT / Devices routes */}
        <Route path="iot" element={lazyRouteElement(<IoTDashboard />, 'IoT dashboard failed to load.')} />
        <Route path="iot/pibots" element={lazyRouteElement(<PiBots />, 'PiBots failed to load.')} />

        {/* Health Check - requires authentication */}
        <Route path="health" element={<HealthCheck />} />

        {/* Update MyStuff routes to ensure proper nesting */}
        <Route path="mystuff/*" element={<MyStuff />} />

        {/* AI Agent routes */}
        <Route path="agents" element={lazyRouteElement(<AgentListPage />, 'Agent list failed to load.')} />
        <Route path="agents/new" element={<AgentBuilderPage />} />
        <Route path="agents/:agentId" element={lazyRouteElement(<AgentDetailPage />, 'Agent detail failed to load.')} />
        <Route path="agents/:agentId/knowledge" element={lazyRouteElement(<KnowledgeBasePage />, 'Knowledge base failed to load.')} />
        <Route path="agents/:agentId/conversations" element={lazyRouteElement(<ConversationLogPage />, 'Conversations failed to load.')} />

        {/* Agent Profile (living agent.md) */}
        <Route path="profile/agent" element={lazyRouteElement(<AgentProfilePage />, 'Agent profile failed to load.')} />
      </Route>

      {/* Admin / Account page — shares AppHeader layout, URL stays /adminpage */}
      <Route path="/adminpage" element={<AdminPage />} />
    </Route>
    {/* <Route path="/manageAuctions" element={<RequireAuth><ManageAuctions /></RequireAuth>} />
    <Route path="/manageArtWork" element={<RequireAuth><ArtworkGridLayout /></RequireAuth>} />
    <Route path="/keys" element={<RequireAuth><KeysLayout /></RequireAuth>} />
    */}
    {/*
    <Route path="/mieserver" element={<RequireAuth><MiEServerAPI /></RequireAuth>} />
    <Route path="/miescheduler" element={<RequireAuth><MiEScheduler /></RequireAuth>} /> */}
    {/* <Route path="/creator" element={<RequireAuth><CreatorPage /></RequireAuth>} />
    <Route path="/return/:accountId" element={<RequireAuth><CreatorPage /></RequireAuth>} /> */}
    {/* OAuth Callback Route - Protected */}
    <Route
      path="/oauth/callback"
      element={
        <RequireAuth>
          <OAuthCallback />
        </RequireAuth>
      }
    />
  </Routes>
);

const routerConfigTest = (
  <Routes>
    {/* Public Routes */}
    <Route path="/" element={<div>Home</div>} />

  </Routes>
);

export default routerConfig;
